<template>
    <div class="set-component">
        <div class="set-header">背景设置</div>
        <div class="set-body">
            <el-tabs v-model="activeTab" class="bg-tabs">
                <!-- ========== Tab 1: 预设壁纸 ========== -->
                <el-tab-pane label="预设壁纸" name="preset">
                    <div class="preset-grid">
                        <div
                            v-for="(img, idx) in presetWallpapers"
                            :key="idx"
                            :class="['preset-item', { active: wallpaperType === 'preset' && wallpaperUrl === img.url }]"
                            @click="applyPresetWallpaper(img.url)"
                        >
                            <div class="preset-preview">
                                <img 
                                    :src="img.url" 
                                    :alt="`壁纸${idx + 1}`" 
                                    loading="lazy"
                                    @error="handleImageError(img, idx)"
                                    :class="{ 'image-error': loadedImages[img.url] === false }"
                                />
                                <span v-if="wallpaperType === 'preset' && wallpaperUrl === img.url" class="preset-check">
                                    <i class="el-icon-check"></i>
                                </span>
                                <div v-if="loadedImages[img.url] === false" class="image-placeholder">
                                    <i class="el-icon-picture-outline"></i>
                                </div>
                            </div>
                        </div>
                    </div>
                </el-tab-pane>

                <!-- ========== Tab 2: 自定义上传 ========== -->
                <el-tab-pane label="自定义上传" name="custom">
                    <div class="custom-tab-content">
                        <!-- 当前背景预览 -->
                        <div v-if="wallpaperType === 'custom' && wallpaperUrl" class="current-bg-preview">
                            <div class="current-bg-title">
                                <i class="el-icon-check-circle"></i>
                                <span>当前使用的背景</span>
                            </div>
                            <div class="current-bg-img">
                                <img :src="wallpaperUrl" alt="当前背景" />
                            </div>
                        </div>

                        <div
                            class="upload-zone"
                            :class="{ 'is-dragover': dragOver }"
                            @click="triggerFileInput"
                            @dragover.prevent="dragOver = true"
                            @dragleave.prevent="dragOver = false"
                            @drop.prevent="onDrop"
                        >
                            <i class="el-icon-picture-outline"></i>
                            <div class="upload-tip">
                                点击或拖拽图片到此处
                            </div>
                            <div class="upload-tip-sub">
                                支持 JPG / PNG，最大 20MB
                            </div>
                        </div>
                        <input
                            ref="fileInput"
                            type="file"
                            accept="image/*"
                            style="display: none"
                            @change="onFileChange"
                        />

                        <div v-if="customPreview" class="custom-preview-wrap">
                            <div class="custom-preview-title">预览</div>
                            <div class="custom-preview-img">
                                <img :src="customPreview" alt="自定义壁纸预览" />
                            </div>
                            <div class="custom-preview-actions">
                                <el-button size="small" @click="cancelCustom">取消</el-button>
                                <el-button size="small" type="primary" @click="applyCustom">应用</el-button>
                            </div>
                        </div>
                    </div>
                </el-tab-pane>

                <!-- ========== Tab 3: 纯色背景 ========== -->
                <el-tab-pane label="纯色背景" name="color">
                    <div class="color-grid">
                        <div
                            v-for="c in presetColors"
                            :key="c.value"
                            :class="['color-item', { active: wallpaperType === 'color' && wallpaperColor === c.value }]"
                            :style="{ background: c.value }"
                            :title="c.label"
                            @click="applyColor(c.value)"
                        >
                            <i v-if="wallpaperType === 'color' && wallpaperColor === c.value" class="el-icon-check"></i>
                        </div>
                    </div>
                </el-tab-pane>
            </el-tabs>

            <!-- ========== 默认重置 ========== -->
            <div class="default-row">
                <span class="default-hint">
                    <i class="el-icon-info"></i>
                    当前：{{ currentDesc }}
                </span>
                <el-button size="small" plain @click="resetDefault">恢复默认</el-button>
            </div>
        </div>
    </div>
</template>

<script>
import { getThemeSetting, updateThemeSetting } from '@/api/settings';
import { uploadImage } from '@/api/upload';
import { getThemeMode, getThemeColor } from '@/utils/theme';

export default {
    name: 'BackgroundSetting',
    data() {
        return {
            activeTab: 'preset',
            wallpaperType: 'default',
            wallpaperUrl: '',
            wallpaperColor: '',
            customPreview: '',
            pendingUploadFile: null,
            dragOver: false,
            presetWallpapers: [
                {url: require('@/assets/img/beijing0.png')} ,
                {url: require('@/assets/img/beijing1.png')} ,
                {url: require('@/assets/img/beijing2.png')} ,
                {url: require('@/assets/img/beijing3.png')} ,
                {url: require('@/assets/img/beijing4.png')} ,
                {url: require('@/assets/img/beijing5.png')} ,
                {url: require('@/assets/img/beijing6.webp')} ,
                {url: require('@/assets/img/beijing7.png')} ,
                {url: require('@/assets/img/beijing8.png')} ,
                {url: require('@/assets/img/beijing9.png')}
            ],
            loadedImages: {},
            presetColors: [
                { value: '#FFFFFF', label: '白色' },
                { value: '#F5F1E8', label: '米白' },
                { value: '#E8EAED', label: '浅灰' },
                { value: '#1E3A5F', label: '深蓝' },
                { value: '#3C2E5E', label: '深紫' },
                { value: '#1E4D3D', label: '墨绿' },
                { value: '#E8743B', label: '暖橙' },
                { value: '#F2B5D4', label: '粉红' }
            ]
        };
    },
    computed: {
        currentDesc() {
            switch (this.wallpaperType) {
                case 'preset':
                    return '预设壁纸';
                case 'custom':
                    return '自定义上传';
                case 'color':
                    return '纯色背景 (' + this.wallpaperColor + ')';
                default:
                    return '默认背景';
            }
        }
    },
    mounted() {
        this.loadBackgroundSetting();
        // 监听主题模式切换事件，主题变化时重新应用背景
        this.$bus && this.$bus.$on('theme-updated', this.handleThemeUpdated);
        // 监听 App.vue 的主题加载完成广播：App.vue 已从后端加载，不要重复请求
        this.$bus && this.$bus.$on('user-theme-loaded', this.handleUserThemeLoaded);
        window.addEventListener('storage', this.handleStorageChange);
    },
    beforeDestroy() {
        this.$bus && this.$bus.$off('theme-updated', this.handleThemeUpdated);
        this.$bus && this.$bus.$off('user-theme-loaded', this.handleUserThemeLoaded);
        window.removeEventListener('storage', this.handleStorageChange);
    },
    methods: {
        handleUserThemeLoaded(data) {
            // App.vue 已从后端加载完，标记自己不要重复加载
            this._skipBackendLoad = true;
        },
        handleImageError(img, idx) {
            console.warn(`壁纸图片加载失败: ${img.url}`);
            this.$set(this.loadedImages, img.url, false);
            // 尝试备用图片
            const backupUrls = [
                'https://neeko-copilot.bytedance.net/api/text_to_image?prompt=beautiful%20nature%20landscape%20wallpaper%20scenery&image_size=landscape_16_9',
                'https://neeko-copilot.bytedance.net/api/text_to_image?prompt=abstract%20colorful%20gradient%20background&image_size=landscape_16_9',
                'https://neeko-copilot.bytedance.net/api/text_to_image?prompt=minimalist%20modern%20wallpaper%20design&image_size=landscape_16_9',
                'https://neeko-copilot.bytedance.net/api/text_to_image?prompt=ocean%20sea%20sunset%20beach%20scenic&image_size=landscape_16_9',
                'https://neeko-copilot.bytedance.net/api/text_to_image?prompt=mountain%20forest%20fog%20misty%20landscape&image_size=landscape_16_9'
            ];
            const backupUrl = backupUrls[idx % backupUrls.length];
            img.url = backupUrl;
        },
        getUserId() {
            try {
                const user = JSON.parse(localStorage.getItem('userInfo'));
                return user ? user.userId || user.id || null : null;
            } catch (e) {
                return null;
            }
        },
        // 将前端 wallpaperType 转为后端格式
        toBackendWallpaperType(type) {
            // 后端只认 'default' 和 'custom'
            if (type === 'custom') return 'custom';
            return 'default'; // preset/color/default 都转成 default
        },
        // 将后端 wallpaperType 转回前端格式
        // 注意：后端 'default' 可能实际是 preset 或 color，需要根据 url/color 反推
        fromBackendWallpaperType(type, url, color) {
            if (type === 'custom') return 'custom';
            // 后端只返回 'default'，但实际内容要靠 url/color 判断
            if (url && url.trim()) return 'preset';
            if (color && color.trim()) return 'color';
            return 'default';
        },
        // 监听主题更新事件
        handleThemeUpdated() {
            this.applyBackground(false);
        },
        // 监听 localStorage 变化
        handleStorageChange(e) {
            if (e.key === 'wallpaperUrl' || e.key === 'wallpaperType' || e.key === 'wallpaperColor') {
                this.wallpaperType = localStorage.getItem('wallpaperType') || 'default';
                this.wallpaperUrl = localStorage.getItem('wallpaperUrl') || '';
                this.wallpaperColor = localStorage.getItem('wallpaperColor') || '';
                this.applyBackground(false);
            }
        },
        async loadBackgroundSetting() {
            // 1) 先从 localStorage 快速恢复（保证离线也能用）
            const cachedType = localStorage.getItem('wallpaperType');
            const cachedUrl = localStorage.getItem('wallpaperUrl') || '';
            const cachedColor = localStorage.getItem('wallpaperColor') || '';

            if (cachedType) {
                this.wallpaperType = cachedType;
                this.wallpaperUrl = cachedUrl;
                this.wallpaperColor = cachedColor;
                // 用 $nextTick 确保在 Vue 批量 DOM 更新之后才操作 body 样式
                await this.$nextTick();
                this.applyBackground(false);
            }

            // 2) 检查 App.vue 是否已经加载过后端数据：
            //    - $bus 事件标记：_skipBackendLoad
            //    - localStorage 后备标记：userThemeLoaded
            //    如果已有标记，说明 App.vue 已从后端加载，不要重复请求
            if (this._skipBackendLoad || localStorage.getItem('userThemeLoaded') === '1') {
                return;
            }

            // 3) 登录状态下，尝试从后端读取最新设置（仅在未加载过的情况下）
            const userId = this.getUserId();
            if (!userId) return;
            try {
                const res = await getThemeSetting(userId);
                if (res && res.data && res.data.code === 200 && res.data.data) {
                    const data = res.data.data;
                    // 后端返回的是 'default'/'custom'，结合 url/color 判断实际类型
                    const url = data.wallpaperUrl || '';
                    const color = data.wallpaperColor || '';
                    const type = this.fromBackendWallpaperType(data.wallpaperType || 'default', url, color);
                    // 仅在后端数据与本地不同时更新（避免覆盖用户刚设置的内容）
                    if (
                        type !== this.wallpaperType ||
                        url !== this.wallpaperUrl ||
                        color !== this.wallpaperColor
                    ) {
                        this.wallpaperType = type;
                        this.wallpaperUrl = url;
                        this.wallpaperColor = color;
                        await this.$nextTick();
                        this.applyBackground(false);
                    }
                }
            } catch (e) {
                // 后端读取失败不阻断 UI
            }
        },
        applyBackground(showTip = true) {
            const body = document.body;

            // 判断目标状态
            let targetType = this.wallpaperType;
            let targetUrl = this.wallpaperUrl;
            let targetColor = this.wallpaperColor;
            let hasWallpaper = false;
            let targetBgImage = '';
            let targetBgColor = '';

            if (targetType === 'preset' && targetUrl) {
                targetBgImage = `url("${targetUrl}")`;
                hasWallpaper = true;
            } else if (targetType === 'custom' && targetUrl) {
                targetBgImage = `url("${targetUrl}")`;
                hasWallpaper = true;
            } else if (targetType === 'color' && targetColor) {
                targetBgColor = targetColor;
                hasWallpaper = false;
            } else {
                // default：回归 global.css 的 --background-color（不手动设值，让 CSS 变量生效）
                hasWallpaper = false;
            }

            // 读取 body 当前实际样式（用于对比是否真的需要改动）
            const currentBgImage = body.style.backgroundImage;
            const currentBgColor = body.style.backgroundColor;
            const currentlyHasWallpaper = body.classList.contains('has-wallpaper');

            const imageChanged = currentBgImage !== targetBgImage;
            const colorChanged = currentBgColor !== targetBgColor;
            const wallpaperClassChanged = currentlyHasWallpaper !== hasWallpaper;

            // 只有真正变了才操作 DOM，避免闪白
            if (imageChanged) body.style.backgroundImage = targetBgImage;
            if (colorChanged) body.style.backgroundColor = targetBgColor;
            if (wallpaperClassChanged) {
                if (hasWallpaper) {
                    body.classList.add('has-wallpaper');
                } else {
                    body.classList.remove('has-wallpaper');
                }
            }

            // 持久化到 localStorage
            localStorage.setItem('wallpaperType', this.wallpaperType);
            localStorage.setItem('wallpaperUrl', this.wallpaperUrl);
            localStorage.setItem('wallpaperColor', this.wallpaperColor);

            if (showTip) {
                this.$message.success('背景已更换');
            }
        },
        async saveToBackend() {
            const userId = this.getUserId();
            if (!userId) return;
            try {
                await updateThemeSetting({
                    userId,
                    // 关键：转成后端格式
                    wallpaperType: this.toBackendWallpaperType(this.wallpaperType),
                    wallpaperUrl: this.wallpaperUrl,
                    wallpaperColor: this.wallpaperColor,
                    themeMode: getThemeMode(),
                    themeColor: getThemeColor()
                });
            } catch (e) {
                // 保存失败不阻断 UI，localStorage 已写入
            }
        },
        async applyPresetWallpaper(url) {
            if (this.wallpaperType === 'preset' && this.wallpaperUrl === url) return;
            this.wallpaperType = 'preset';
            this.wallpaperUrl = url;
            this.wallpaperColor = '';
            this.applyBackground(true);
            await this.saveToBackend();
        },
        async applyColor(color) {
            if (this.wallpaperType === 'color' && this.wallpaperColor === color) return;
            this.wallpaperType = 'color';
            this.wallpaperColor = color;
            this.wallpaperUrl = '';
            this.applyBackground(true);
            await this.saveToBackend();
        },
        async resetDefault() {
            this.wallpaperType = 'default';
            this.wallpaperUrl = '';
            this.wallpaperColor = '';
            this.customPreview = '';
            this.applyBackground(true);
            await this.saveToBackend();
        },
        triggerFileInput() {
            if (this.$refs.fileInput) {
                this.$refs.fileInput.click();
            }
        },
        onFileChange(e) {
            const file = e.target && e.target.files && e.target.files[0];
            this.handleFile(file);
            // 清空 input，保证同一文件可以再次选择
            if (e.target) e.target.value = '';
        },
        onDrop(e) {
            this.dragOver = false;
            const file = e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0];
            this.handleFile(file);
        },
        handleFile(file) {
            if (!file) return;
            if (!file.type.startsWith('image/')) {
                this.$message.error('请选择图片文件');
                return;
            }
            if (file.size > 20 * 1024 * 1024) {
                this.$message.error('图片大小不能超过 20MB');
                return;
            }
            // 保存原始 file 对象供后续上传
            this.pendingUploadFile = file;
            // 仅用于本地预览，不持久化
            const reader = new FileReader();
            reader.onload = (evt) => {
                this.customPreview = evt.target.result;
            };
            reader.readAsDataURL(file);
        },
        cancelCustom() {
            this.customPreview = '';
            this.pendingUploadFile = null;
        },
        async applyCustom() {
            if (!this.pendingUploadFile) {
                this.$message.warning('请先选择图片');
                return;
            }
            try {
                this.$message.info('正在上传图片...');
                // 1. 上传图片文件到后端，拿到 URL
                const res = await uploadImage(this.pendingUploadFile);
                if (!res || !res.data || res.data.code !== 200 || !res.data.data || !res.data.data.url) {
                    this.$message.error('图片上传失败，请重试');
                    return;
                }
                const imageUrl = res.data.data.url;
                // 2. 使用 URL 作为壁纸地址
                this.wallpaperType = 'custom';
                this.wallpaperUrl = imageUrl;
                this.wallpaperColor = '';
                this.customPreview = '';
                this.pendingUploadFile = null;
                // 3. 应用到 body（localStorage 只存 URL，不存 base64）
                this.applyBackground(true);
                // 4. 同步到后端数据库
                await this.saveToBackend();
            } catch (e) {
                console.error('上传背景图片失败：', e);
                this.$message.error('图片上传失败，请重试');
            }
        }
    }
};
</script>

<style scoped>
.set-component {
    background: var(--card-background);
    border-radius: 12px;
    border: 1px solid var(--border-color-lighter);
}
.set-header {
    padding: 18px 24px;
    font-size: 16px;
    font-weight: 600;
    color: var(--text-color);
    border-bottom: 1px solid var(--border-color-lighter);
}
.set-body {
    padding: 20px 24px 24px;
}
.bg-tabs .el-tabs__item {
    font-size: 14px;
}

/* ========== 预设壁纸网格 ========== */
.preset-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 14px;
    padding: 4px 2px;
}
.preset-item {
    cursor: pointer;
    user-select: none;
}
.preset-preview {
    position: relative;
    width: 100%;
    height: 100px;
    border-radius: 8px;
    overflow: hidden;
    border: 3px solid transparent;
    box-sizing: border-box;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
    transition: transform 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease;
    background: var(--background-color-soft);
}
.preset-preview img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
}
.preset-preview img.image-error {
    display: none;
}
.image-placeholder {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, #f5f7fa 0%, #e4e8ec 100%);
    color: #909399;
    font-size: 28px;
}
.preset-item:hover .preset-preview {
    transform: scale(1.03);
    border-color: var(--theme-color, #409EFF);
}
.preset-item.active .preset-preview {
    border-color: var(--theme-color, #409EFF);
    box-shadow: 0 0 0 2px var(--theme-color-light, rgba(64, 158, 255, 0.15));
}
.preset-check {
    position: absolute;
    right: 6px;
    top: 6px;
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background: var(--theme-color, #409EFF);
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15);
}

/* ========== 自定义上传容器（带滚动条）========== */
.custom-tab-content {
    max-height: 500px;
    overflow-y: auto;
    padding-right: 8px;
}
.custom-tab-content::-webkit-scrollbar {
    width: 6px;
}
.custom-tab-content::-webkit-scrollbar-track {
    background: var(--background-color-soft);
    border-radius: 3px;
}
.custom-tab-content::-webkit-scrollbar-thumb {
    background: var(--border-color);
    border-radius: 3px;
}
.custom-tab-content::-webkit-scrollbar-thumb:hover {
    background: var(--text-color-muted);
}

/* ========== 当前背景预览 ========== */
.current-bg-preview {
    margin-bottom: 16px;
    padding: 14px;
    border: 1px solid var(--border-color-lighter);
    border-radius: 10px;
    background: var(--background-color-soft);
}
.current-bg-title {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    color: var(--text-color-secondary);
    margin-bottom: 10px;
}
.current-bg-title i {
    color: var(--success-color, #67c23a);
}
.current-bg-img {
    border-radius: 8px;
    overflow: hidden;
    background: var(--background-color-muted);
    max-height: 200px;
}
.current-bg-img img {
    width: 100%;
    height: auto;
    max-height: 200px;
    object-fit: cover;
    display: block;
}

/* ========== 自定义上传 ========== */
.upload-zone {
    border: 2px dashed var(--border-color);
    border-radius: 10px;
    padding: 28px 16px;
    text-align: center;
    color: var(--text-color-secondary);
    cursor: pointer;
    transition: border-color 0.2s ease, background-color 0.2s ease;
    background: var(--background-color-soft);
}
.upload-zone:hover,
.upload-zone.is-dragover {
    border-color: var(--theme-color, #409EFF);
    color: var(--theme-color, #409EFF);
    background: var(--theme-color-light, rgba(64, 158, 255, 0.08));
}
.upload-zone i {
    font-size: 42px;
}
.upload-tip {
    margin-top: 6px;
    font-size: 14px;
    color: var(--text-color);
}
.upload-tip-sub {
    margin-top: 4px;
    font-size: 12px;
    color: var(--text-color-muted);
}
.custom-preview-wrap {
    margin-top: 20px;
    padding: 14px;
    border: 1px solid var(--border-color-lighter);
    border-radius: 10px;
    background: var(--background-color-soft);
}
.custom-preview-title {
    font-size: 13px;
    color: var(--text-color-secondary);
    margin-bottom: 10px;
}
.custom-preview-img {
    border-radius: 8px;
    overflow: hidden;
    background: var(--background-color-muted);
    max-height: 260px;
}
.custom-preview-img img {
    width: 100%;
    height: auto;
    max-height: 260px;
    object-fit: cover;
    display: block;
}
.custom-preview-actions {
    margin-top: 12px;
    display: flex;
    gap: 10px;
    justify-content: flex-end;
}

/* ========== 纯色块 ========== */
.color-grid {
    display: grid;
    grid-template-columns: repeat(8, 1fr);
    gap: 12px;
    padding: 4px 2px;
}
.color-item {
    aspect-ratio: 1 / 1;
    border-radius: 8px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    font-size: 16px;
    border: 3px solid transparent;
    box-sizing: border-box;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.12);
    transition: transform 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease;
}
.color-item:hover {
    transform: scale(1.06);
    border-color: var(--theme-color, #409EFF);
}
.color-item.active {
    border-color: var(--theme-color, #409EFF);
    box-shadow: 0 0 0 2px var(--theme-color-light, rgba(64, 158, 255, 0.2));
}

/* ========== 默认重置行 ========== */
.default-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-top: 20px;
    padding-top: 16px;
    border-top: 1px dashed var(--border-color-lighter);
    font-size: 13px;
    color: var(--text-color-secondary);
}
.default-hint {
    display: flex;
    align-items: center;
    gap: 6px;
}
.default-hint i {
    color: var(--theme-color);
}
</style>
